import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import BrandMark from '../../components/BrandMark';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // H-12: Strip the reset token from the visible URL so it doesn't leak to
  // browser history, Referer headers, or third-party scripts loaded on the page.
  useEffect(() => {
    if (typeof window !== 'undefined' && token && window.location.search) {
      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (_e) { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [status, setStatus] = useState('validating'); // validating, valid, invalid, expired, success
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }

    // Decode token to check expiry (basic client-side check)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      
      if (Date.now() > exp) {
        setStatus('expired');
      } else if (payload.type !== 'password_reset') {
        setStatus('invalid');
      } else {
        setStatus('valid');
      }
    } catch (e) {
      setStatus('invalid');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/partner/reset-password`, {
        reset_token: token,
        new_password: formData.password
      });

      // Store the token for auto-login
      if (response.data.token) {
        localStorage.setItem('partnerToken', response.data.token);

        if (response.data.refresh_token) {

          localStorage.setItem('partnerAccessToken', response.data.access_token || response.data.token);

          localStorage.setItem('partnerRefreshToken', response.data.refresh_token);

        }
        localStorage.setItem('partnerId', response.data.partner?.id);
        localStorage.setItem('partnerEmail', response.data.partner?.email);
      }

      setStatus('success');
      toast.success('Password reset successful!');

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/partner/dashboard');
      }, 2000);

    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      
      if (message.includes('expired')) {
        setStatus('expired');
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'validating':
        return (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Validating reset link...</p>
          </div>
        );

      case 'invalid':
        return (
          <div className="text-center py-12">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Invalid Reset Link</h2>
            <p className="text-slate-400 mb-6">
              This password reset link is invalid or has already been used.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
            >
              Go to Homepage
            </Button>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center py-12">
            <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Link Expired</h2>
            <p className="text-slate-400 mb-6">
              This password reset link has expired. Please request a new one.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
            >
              Request New Link
            </Button>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-12">
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Password Reset Successful!</h2>
            <p className="text-slate-400 mb-6">
              Your password has been updated. Redirecting to dashboard...
            </p>
            <Loader2 className="w-6 h-6 text-amber-400 animate-spin mx-auto" />
          </div>
        );

      case 'valid':
      default:
        return (
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
            <div>
              <Label className="text-slate-300 mb-2 block">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter new password (min 8 characters)"
                  className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-slate-300 mb-2 block">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              data-testid="reset-password-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-900/80 border-slate-800">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <BrandMark size="lg" showText={false} />
          </div>
          <CardTitle className="text-2xl font-bold text-white">
            Reset Password
          </CardTitle>
          <p className="text-slate-400 text-sm mt-1">Partner Account</p>
        </CardHeader>
        <CardContent className="pt-6">
          {renderContent()}
        </CardContent>
      </Card>
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default PartnerResetPassword;
